import { teammateDisplayName } from '@littlebird/action-cards';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { AvatarStack } from '@/components/ui/Avatar';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { FEATURES, useFeature } from '@/features/feature-flags';
import type { HivemindGroup } from '@/features/hivemind/hivemind.types';

import { PersonAvatar } from '../components/person-avatar';
import { SectionHeading } from '../components/section-heading';
import { usePeopleWikiQuery } from '../people-wiki.query';

import { GroupDialog } from './group-dialog';
import {
  sameText,
  useDeleteGroupMutation,
  useGroupsQuery,
} from './groups.query';

const SHOWN_AVATARS = 5;

/** The saved groups of people that the user messages together, above the People Wiki table: a row for each group, with
 * its people as stacked avatars, and a menu to edit or delete it. */
export const PeopleGroups = () => {
  const hasFeature = useFeature();
  const enabled = hasFeature(FEATURES.HIVE) && hasFeature(FEATURES.PEOPLE_WIKI);
  const { data: groups = [] } = useGroupsQuery({ enabled });
  const { data: people = [] } = usePeopleWikiQuery({ enabled });
  const { mutate: deleteGroup } = useDeleteGroupMutation();
  const [editing, setEditing] = useState<HivemindGroup | 'new' | null>(null);

  if (!enabled) return null;

  const avatarUrlOf = (email: string) =>
    people.find(({ littlebirdAccounts }) =>
      littlebirdAccounts.some((account) => sameText(account.email, email))
    )?.avatarUrl ?? null;

  return (
    <YStack className="gap-2">
      <XStack className="items-center justify-between">
        <SectionHeading title="Groups" />
        <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
          <Icons.plus className="size-3.5" />
          New group
        </Button>
      </XStack>
      {groups.length > 0 && (
        <YStack className="divide-y divide-border rounded-xl border border-border">
          {groups.map((group) => (
            <XStack key={group.name} className="items-center gap-3 px-4 py-2">
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                {group.name}
              </span>
              <AvatarStack>
                {group.members.slice(0, SHOWN_AVATARS).map((member) => (
                  <PersonAvatar
                    key={member.id}
                    person={{
                      id: String(member.id),
                      name: teammateDisplayName(member),
                      avatarUrl: avatarUrlOf(member.email),
                      // No email, so the colour comes from the id, as it does in the chat
                      primaryEmail: null,
                    }}
                    sizeClassName="size-6"
                  />
                ))}
              </AvatarStack>
              <span className="w-16 text-xs text-foreground-subtle">
                {group.members.length}{' '}
                {group.members.length === 1 ? 'person' : 'people'}
              </span>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger
                  variant="ghost"
                  aria-label={`Actions for ${group.name}`}
                  className="size-8 justify-center p-0"
                >
                  <Icons.moreHorizontal className="size-4" />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Item onSelect={() => setEditing(group)}>
                    Edit
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={() => deleteGroup(group.name)}>
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </XStack>
          ))}
        </YStack>
      )}
      {editing && (
        <GroupDialog
          group={editing === 'new' ? null : editing}
          groups={groups}
          people={people}
          onClose={() => setEditing(null)}
        />
      )}
    </YStack>
  );
};
